import type { BadgeVariant } from '../../components/Badge/Badge';
import type {
  ChecklistItem,
  DocumentType,
  PolicyStatus,
  UnderwritingStatus,
} from './types';

export interface StatusMeta {
  label: string;
  badge: BadgeVariant;
  description: string;
  /** Position in the linear workflow; terminal states share the last index. */
  stageIndex: number;
}

export const STATUS_META: Record<PolicyStatus, StatusMeta> = {
  draft: {
    label: 'Draft',
    badge: 'secondary',
    description: 'Policy has been created from an approved quote and is being prepared.',
    stageIndex: 1,
  },
  pending_underwriting: {
    label: 'Pending Underwriting',
    badge: 'warning',
    description: 'Submitted to underwriting and awaiting a risk decision.',
    stageIndex: 2,
  },
  approved: {
    label: 'Issuance pending',
    badge: 'info',
    description: 'Underwriting approved. Binder generation and issuance pending.',
    stageIndex: 3,
  },
  issued: {
    label: 'Issued',
    badge: 'default',
    description: 'Policy documents issued. Awaiting the effective date to go active.',
    stageIndex: 3,
  },
  active: {
    label: 'Active',
    badge: 'success',
    description: 'Policy is in force and providing coverage.',
    stageIndex: 4,
  },
  cancelled: {
    label: 'Cancelled',
    badge: 'error',
    description: 'Policy has been cancelled and is no longer in force.',
    stageIndex: 4,
  },
  renewed: {
    label: 'Renewed',
    badge: 'outline',
    description: 'Policy term completed and continued under a renewal policy.',
    stageIndex: 4,
  },
};

export const POLICY_STATUS_ORDER: PolicyStatus[] = [
  'draft',
  'pending_underwriting',
  'approved',
  'issued',
  'active',
  'renewed',
  'cancelled',
];

/**
 * Linear happy-path stages used by the workflow stepper. `approved` and `issued` share the
 * Issuance stage: approval alone is not a milestone the user acts on separately — the binder
 * and issuance actions both happen there.
 */
export const WORKFLOW_STAGES: { key: string; label: string }[] = [
  { key: 'quote', label: 'Approved Quote' },
  { key: 'draft', label: 'Draft Policy' },
  { key: 'underwriting', label: 'Underwriting' },
  { key: 'issuance', label: 'Issuance' },
  { key: 'active', label: 'Active' },
];

export const UNDERWRITING_STATUS_META: Record<
  UnderwritingStatus,
  { label: string; badge: BadgeVariant }
> = {
  not_started: { label: 'Not Started', badge: 'secondary' },
  in_review: { label: 'In Review', badge: 'warning' },
  approved: { label: 'Approved', badge: 'success' },
  declined: { label: 'Declined', badge: 'error' },
  referred: { label: 'Referred', badge: 'info' },
};

export const DOCUMENT_TYPE_META: Record<
  DocumentType,
  { label: string; badge: BadgeVariant; icon: string }
> = {
  binder: { label: 'Binder', badge: 'info', icon: 'binder' },
  policy_schedule: { label: 'Policy Schedule', badge: 'default', icon: 'schedule' },
  certificate_of_insurance: { label: 'Certificate of Insurance', badge: 'success', icon: 'coi' },
  // Wire value stays `quote` (the API sends `{"type": "quote"}`); only the label reads "Application".
  quote: { label: 'Application', badge: 'secondary', icon: 'quote' },
  endorsement: { label: 'Endorsement', badge: 'warning', icon: 'endorsement' },
  invoice: { label: 'Invoice', badge: 'outline', icon: 'invoice' },
};

export const PRODUCTS: string[] = [
  'Paid Family & Medical Leave',
  'Parental Leave Top-Up',
  'Short-Term Disability',
  'Supplemental Caregiver Leave',
];

export const CANCELLATION_REASONS: string[] = [
  'Insured request',
  'Non-payment of premium',
  'Replaced by other coverage',
  'Material misrepresentation',
  'Business closed / no longer eligible',
  'Other',
];

/**
 * Checklist item ids that block an underwriting decision until completed.
 * All items are currently optional (empty list); the PAS backend owns the
 * source of truth and will be updated to match.
 */
export const REQUIRED_CHECKLIST_ITEM_IDS: readonly string[] = [];

const REQUIRED_CHECKLIST_ITEM_ID_SET = new Set<string>(REQUIRED_CHECKLIST_ITEM_IDS);

const UNDERWRITING_CHECKLIST_ITEMS: { id: string; label: string }[] = [
  { id: 'census', label: 'Census reviewed and validated' },
  { id: 'financials', label: 'Employer financials / stability confirmed' },
  { id: 'loss_history', label: 'Prior leave / loss history obtained' },
  { id: 'eligibility', label: 'State eligibility & statutory overlap checked' },
  { id: 'pricing', label: 'Pricing within rating guidelines' },
  { id: 'concurrency', label: 'Concurrency & offset assumptions verified' },
  { id: 'broker_kyc', label: 'Broker appointment / KYC on file' },
];

/** Fresh underwriting checklist applied to each new submission. */
export const DEFAULT_UNDERWRITING_CHECKLIST: Omit<ChecklistItem, 'done'>[] =
  UNDERWRITING_CHECKLIST_ITEMS.map((item) => ({
    ...item,
    required: REQUIRED_CHECKLIST_ITEM_ID_SET.has(item.id),
  }));

/**
 * Normalize a checklist's `required` flags so only {@link REQUIRED_CHECKLIST_ITEM_IDS}
 * gate an underwriting decision, regardless of what the backend reports. UI-side
 * override — see NewPolicyModal / UnderwritingDecisionPanel consumers.
 */
export const applyChecklistRequirements = <T extends { id: string }>(items: T[]): T[] =>
  items.map((item) => ({ ...item, required: REQUIRED_CHECKLIST_ITEM_ID_SET.has(item.id) }));

/**
 * PAS backend endpoints. All paths sit behind `VITE_API_BASE_URL` and follow the
 * SPA convention of an `/api` prefix (see quotes/policies services). Reference
 * data is nested under the same base as the policies collection.
 */
const PAS_BASE = '/api/policy-admin';

export const PAS_ENDPOINTS = {
  POLICIES: `${PAS_BASE}/policies/`,
  POLICY: (id: string) => `${PAS_BASE}/policies/${id}/`,
  FROM_QUOTE: `${PAS_BASE}/policies/from-quote/`,
  SUBMIT_TO_UNDERWRITING: (id: string) => `${PAS_BASE}/policies/${id}/submit-to-underwriting/`,
  GENERATE_BINDER: (id: string) => `${PAS_BASE}/policies/${id}/generate-binder/`,
  ISSUE: (id: string) => `${PAS_BASE}/policies/${id}/issue/`,
  ACTIVATE: (id: string) => `${PAS_BASE}/policies/${id}/activate/`,
  CHECKLIST_ITEM: (id: string, itemId: string) => `${PAS_BASE}/policies/${id}/checklist/${itemId}/`,
  UNDERWRITING_DECISIONS: (id: string) => `${PAS_BASE}/policies/${id}/underwriting-decisions/`,
  CANCEL: (id: string) => `${PAS_BASE}/policies/${id}/cancel/`,
  RENEW: (id: string) => `${PAS_BASE}/policies/${id}/renew/`,
  NOTES: (id: string) => `${PAS_BASE}/policies/${id}/notes/`,
  NOTE: (id: string, noteId: string) => `${PAS_BASE}/policies/${id}/notes/${noteId}/`,
  REGENERATE_DOCUMENT: (id: string, docId: string) =>
    `${PAS_BASE}/policies/${id}/documents/${docId}/regenerate/`,
  BROKERS: `${PAS_BASE}/brokers/`,
  CARRIERS: `${PAS_BASE}/carriers/`,
  USERS: `${PAS_BASE}/users/`,
  PRODUCTS: `${PAS_BASE}/products/`,
  WORKFLOW_SETTINGS: `${PAS_BASE}/workflow-settings/`,
  APPROVED_QUOTES: `${PAS_BASE}/approved-quotes/`,
} as const;

export interface PasNavItem {
  key: string;
  label: string;
  to: string;
  icon: string;
  end?: boolean;
}

export const PAS_NAV: PasNavItem[] = [
  { key: 'dashboard', label: 'Dashboard', to: '/policy-admin', icon: 'dashboard', end: true },
  { key: 'policies', label: 'Policies', to: '/policy-admin/policies', icon: 'policies' },
  { key: 'underwriting', label: 'Underwriting Queue', to: '/policy-admin/underwriting', icon: 'underwriting' },
  { key: 'renewals', label: 'Renewals', to: '/policy-admin/renewals', icon: 'renewals' },
  { key: 'documents', label: 'Documents', to: '/policy-admin/documents', icon: 'documents' },
  { key: 'search', label: 'Search', to: '/policy-admin/search', icon: 'search' },
  // "Administration" hidden from the nav — route (/policy-admin/administration) and backend stay intact.
  // { key: 'administration', label: 'Administration', to: '/policy-admin/administration', icon: 'administration' },
];
